import {
  GOLDEN_ANGLE_DEG,
  clamp,
  type PackingKind,
  packingKind,
  spokeHint,
  visibleParastichies,
} from "./phyllotaxis";

export type ColorMode = "kernel" | "family" | "radius";

export type AppState = {
  degrees: number;
  seeds: number;
  overlay: boolean;
  petals: boolean;
  color: ColorMode;
};

export const MIN_DEG = 0.1;
export const MAX_DEG = 180;
export const MIN_SEEDS = 40;
export const MAX_SEEDS = 2400;
export const DEFAULT_STATE: AppState = {
  degrees: 137.5,
  seeds: 900,
  overlay: false,
  petals: true,
  color: "kernel",
};

const COLOR_MODES: ColorMode[] = ["kernel", "family", "radius"];

export function isColorMode(v: string): v is ColorMode {
  return (COLOR_MODES as string[]).includes(v);
}

export function sanitize(partial: Partial<AppState>): AppState {
  const degrees = clamp(Number(partial.degrees ?? DEFAULT_STATE.degrees), MIN_DEG, MAX_DEG);
  const seeds = Math.round(clamp(Number(partial.seeds ?? DEFAULT_STATE.seeds), MIN_SEEDS, MAX_SEEDS));
  const color = partial.color && isColorMode(partial.color) ? partial.color : DEFAULT_STATE.color;
  return {
    degrees: Number.isFinite(degrees) ? roundDeg(degrees) : DEFAULT_STATE.degrees,
    seeds: Number.isFinite(seeds) ? seeds : DEFAULT_STATE.seeds,
    overlay: Boolean(partial.overlay ?? DEFAULT_STATE.overlay),
    petals: Boolean(partial.petals ?? DEFAULT_STATE.petals),
    color,
  };
}

export function roundDeg(deg: number): number {
  return Math.round(deg * 1000) / 1000;
}

export function parseHash(hash: string): Partial<AppState> {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  const q = new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
  const out: Partial<AppState> = {};
  if (q.has("deg")) out.degrees = Number(q.get("deg"));
  if (q.has("n")) out.seeds = Number(q.get("n"));
  if (q.has("overlay")) out.overlay = q.get("overlay") === "1";
  if (q.has("petals")) out.petals = q.get("petals") !== "0";
  const color = q.get("color");
  if (color && isColorMode(color)) out.color = color;
  return out;
}

export function toHash(state: AppState): string {
  const q = new URLSearchParams();
  q.set("deg", String(roundDeg(state.degrees)));
  q.set("n", String(state.seeds));
  if (state.overlay) q.set("overlay", "1");
  if (!state.petals) q.set("petals", "0");
  if (state.color !== "kernel") q.set("color", state.color);
  return `#${q.toString()}`;
}

export type Reading = {
  kind: PackingKind;
  errorDeg: number;
  parastichies: { a: number; b: number };
  spokes: { q: number; errorDeg: number } | null;
  title: string;
  body: string;
};

export function readingFor(state: AppState): Reading {
  const kind = packingKind(state.degrees, state.seeds);
  const errorDeg = Math.abs(state.degrees - GOLDEN_ANGLE_DEG);
  const parastichies = visibleParastichies(state.degrees, state.seeds);
  const spokes = spokeHint(state.degrees, state.seeds);
  if (kind === "golden") {
    return {
      kind,
      errorDeg,
      parastichies,
      spokes,
      title: "Packed. Fibonacci spirals.",
      body: `The golden turn is ${GOLDEN_ANGLE_DEG.toFixed(6)}°. New seeds never line up with old ones, so the head fills without gaps. You are seeing about ${parastichies.a} and ${parastichies.b} spirals — consecutive Fibonacci numbers.`,
    };
  }
  if (kind === "near-golden") {
    return {
      kind,
      errorDeg,
      parastichies,
      spokes,
      title: "Still packed. Nudge it.",
      body: `137.5° sits ${errorDeg.toFixed(3)}° from the golden turn. The head still looks like a sunflower. Press +0.1° or −0.1° and the spirals collapse into spokes.`,
    };
  }
  if (kind === "spokes" && spokes) {
    return {
      kind,
      errorDeg,
      parastichies,
      spokes,
      title: `Spokes. ${spokes.q}-fold.`,
      body: `This turn is close to a rational cut of the circle, so seeds stack on ${spokes.q} rays instead of filling the disc. The golden angle is the most irrational cut — the one that refuses this alignment.`,
    };
  }
  return {
    kind,
    errorDeg,
    parastichies,
    spokes,
    title: "The pattern broke.",
    body: `${state.degrees.toFixed(3)}° is ${errorDeg.toFixed(3)}° from golden. Visible families sit near ${parastichies.a} and ${parastichies.b}. Drag until the gaps close, or jump back to the golden turn.`,
  };
}
